import type { UseFormReturnType } from '@mantine/form';
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AuthApi,
  clearSessionData,
  getSessionState,
  transitionSession,
  requireAuthentication,
  startAuthentication,
  useLoginStatus,
} from '@/entities/user/session';
import { type AuthRequest, LoginStatus } from '@/entities/user/user';
import {
  getErrorStatus,
  type InterceptedError,
} from '@/shared/api/intercepted-error';
import { CHANGE_PASSWORD, HOME, LOGIN } from '@/shared/config/routes';
import { requestError } from '@/shared/ui/notifications';

export const useLogin = (
  form: UseFormReturnType<AuthRequest>,
  prepareApplication: (queryClient: QueryClient) => Promise<boolean>,
  loginPage?: boolean,
  getRedirectUrl?: () => Record<string, string>,
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const loginStatus = useLoginStatus();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!loginPage || pathname !== LOGIN || loginStatus !== LoginStatus.login)
      return;

    // URLSearchParams already decodes the value, including percent signs in URLs.
    const next = searchParams.get('next') || HOME;
    if (/^https?:\/\//.test(next)) {
      window.location.replace(next);
    } else {
      const isInternalPath = next.startsWith('/') && !next.startsWith('//');
      const isLoginPath = next.split(/[?#]/, 1)[0] === LOGIN;
      navigate(isInternalPath && !isLoginPath ? next : HOME, { replace: true });
    }
  }, [loginPage, loginStatus, pathname, searchParams, navigate]);

  const retryPreparation = useCallback(
    () => prepareApplication(queryClient),
    [prepareApplication, queryClient],
  );

  const mutation = useMutation({
    mutationKey: ['login'],
    retry: false,
    mutationFn: async ({
      payload,
    }: {
      payload: AuthRequest;
      session: ReturnType<typeof getSessionState>;
    }) => {
      const { redirect_url, merge_redirect_url } = getRedirectUrl?.() || {};
      const response = await AuthApi.loginUnion(
        payload,
        redirect_url,
        merge_redirect_url,
      );
      if (response.request?.responseURL?.includes('/auth/merge')) {
        window.location.href = merge_redirect_url;
        return false;
      }
      return true;
    },
    onSuccess: async (authenticated, { session }) => {
      if (!authenticated || getSessionState(queryClient) !== session) return;
      const preparingState = transitionSession(
        queryClient,
        LoginStatus.preparing,
      );
      await clearSessionData(queryClient);
      if (getSessionState(queryClient) !== preparingState) return;
      await retryPreparation();
    },
    onError(error: InterceptedError, { session }) {
      if (getSessionState(queryClient) !== session) return;
      requestError(error);
      if (getErrorStatus(error) === 423) {
        queryClient.setQueryDefaults(['profile'], {
          cacheTime: Infinity,
          staleTime: Infinity,
        });
        queryClient.setQueryData(['profile'], {
          id: error.response?.data?.['user_id'],
        });
        transitionSession(queryClient, LoginStatus.passwordExpired);
        navigate(CHANGE_PASSWORD, { replace: true });
        return;
      }
      requireAuthentication(queryClient);
      const fields = new Set(['login', 'password', 'ldap_id']);
      error.response?.data?.invalid_params?.forEach(({ loc, msg }) => {
        const field = loc?.at(-1);
        if (fields.has(field)) form.setFieldError(field, msg);
      });
      error.response?.data?.errors?.forEach((entry) => {
        const field = Array.isArray(entry) ? entry[0] : entry;
        if (fields.has(field))
          form.setFieldError(field, error.response?.data?.title);
      });
    },
  });

  const login = (payload: AuthRequest) => {
    const attempt = startAuthentication(queryClient);
    if (!attempt) return;
    mutation.mutate({ payload, session: attempt.session });
  };

  return {
    login,
    isPending: mutation.isPending,
    isError: mutation.isError,
    retryPreparation,
  };
};

export const useLogout = (withFrom = true) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const finishLogout = async () => {
    await clearSessionData(queryClient);
    transitionSession(queryClient, LoginStatus.unauthorized);
    const { pathname, search } = window.location;
    const from = ![HOME, LOGIN, CHANGE_PASSWORD].includes(pathname)
      ? `${pathname}${search}`
      : '';
    navigate(
      withFrom && from ? `${LOGIN}?next=${encodeURIComponent(from)}` : LOGIN,
      { replace: true },
    );
  };

  const { mutate } = useMutation({
    mutationKey: ['logout'],
    retry: false,
    mutationFn: () => AuthApi.logout().then((response) => response.data),
    onSuccess: finishLogout,
    onError: async (error: InterceptedError) => {
      if (getErrorStatus(error) !== 401) requestError(error);
      await finishLogout();
    },
  });

  return useCallback(() => {
    if (getSessionState(queryClient).loginStatus === LoginStatus.loggingOut)
      return;
    transitionSession(queryClient, LoginStatus.loggingOut);
    void queryClient.cancelQueries();
    mutate();
  }, [mutate, queryClient]);
};
